import React, { useContext, useState, useEffect } from "react";
import "./scss/style.scss";
import { initialState, reducer } from "./utils/app-state";
import { useAppContext, AppContext } from "./utils/context/app-context";
import {
	createBrowserRouter,
	RouterProvider,
	useLocation,
	Navigate,
	Outlet,
	useParams,
	useNavigate
} from "react-router-dom";
import secureLocalStorage from "react-secure-storage";
import AuthLayout from "./layouts/AuthLayout";
import MainLayout from "./layouts/MainLayout";
import Login from "./pages/Login";
import { API_URL } from "./utils/config";
import { useImmerReducer } from "use-immer";
import Dashboard from "./pages/Dashboard";
import CoursePage from "./pages/CoursePage";
import StudentFormsAccessPage from "./pages/StudentFormsAccessPage";
import AttendanceRegisterPage from "./pages/AttendanceRegisterPage";
import EditStudentPage from "./pages/EditStudentPage";
import CreateStudentPage from "./pages/CreateStudentPage";
import HealthQuestionnairePage from "./pages/HealthQuestionnairePage";
import DailyRiskAssessmentPage from "./pages/DailyRiskAssessmentPage";
import TeachingLogPage from "./pages/TeachingLogPage";
import EvaluationFormsPage from "./pages/EvaluationFormsPage";
import EvaluationFormPage from "./pages/EvaluationFormPage";
import PhysicalCompetencyPage from "./pages/PhysicalCompetencyPage";
import ReferralFormPage from "./pages/ReferralFormPage";
import IncidentReportPage from "./pages/IncidentReportPage";
import IncidentsWitnessedPage from "./pages/IncidentsWitnessedPage";

import WrittenAssessmentsPage from "./pages/WrittenAssessmentsPage";
import WrittenAssessmentPage from "./pages/WrittenAssessmentPage";
import UpcomingCoursesPage from "./pages/UpcomingCoursesPage";
import PreviousTeachingLogsPage from "./pages/PreviousTeachingLogsPage";
import WitnessIncidentPage from "./pages/WitnessIncidentPage";
import ForgottenPassword from "./pages/ForgottenPassword";
import ResetPassword from "./pages/ResetPassword";
import ErrorPage from "./pages/ErrorPage";
import { LoadingSpinner } from "./components/UiComponents";
import CertificatesPage from "./pages/CertificatesPage";
import LoginAsTutor from "./pages/LoginAsTutor";


const router = createBrowserRouter([
	{
		element: <RequireAuth />,
		errorElement: <ErrorPage />,
		children: [
			{
				path: "/logout",
				element: <LogoutComponent />,
			},
			{
				element: <MainLayout />,
				children: [
					{
						element: <CoursesLoader />,
						children: [
							{ path: "/", element: <Dashboard /> },
							{
								path: "/upcoming-courses",
								element: <UpcomingCoursesPage />,
							},
							{
								path: "/teaching-logs",
								element: <PreviousTeachingLogsPage />,
							},
							{
								element: <CourseLoader />,
								children: [
									{
										path: "/courses/:courseId",
										element: <CoursePage />,
									},
									{
										path: "/courses/:courseId/evaluation-forms",
										element: <EvaluationFormsPage />,
									},
									{
										path: "/courses/:courseId/evaluation-forms/:formId",
										element: <EvaluationFormPage />,
									},
									{
										path: "/courses/:courseId/participant-forms-access",
										element: <StudentFormsAccessPage />,
									},
									{
										path: "/courses/:courseId/attendance-register",
										element: <AttendanceRegisterPage />,
									},
									{
										path: "/courses/:courseId/daily-risk-assessment",
										element: <DailyRiskAssessmentPage />,
									},
									{
										path: "/courses/:courseId/participants/:studentId",
										element: <EditStudentPage />,
									},
									{
										path: "/courses/:courseId/participants/create",
										element: <CreateStudentPage />,
									},
									{
										path: "/courses/:courseId/health-questionnaire/:questionnaireId",
										element: <HealthQuestionnairePage />,
									},
									{
										path: "/courses/:courseId/physical-competency-assessment/:studentId",
										element: <PhysicalCompetencyPage />,
									},
									{
										path: "/courses/:courseId/teaching-log",
										element: <TeachingLogPage />,
									},
									{
										path: "/courses/:courseId/refer/:studentId",
										element: <ReferralFormPage />,
									},
									{
										path: "/courses/:courseId/report-incident",
										element: <IncidentReportPage />,
									},
									{
										path: "/courses/:courseId/incidents-witnessed",
										element: <IncidentsWitnessedPage />,
									},
									{
										path: "/courses/:courseId/incidents-witnessed/:incidentId",
										element: <IncidentReportPage />,
									},
									{
										path: "/courses/:courseId/written-assessments",
										element: <WrittenAssessmentsPage />,
									},
									{
										path: "/courses/:courseId/written-assessments/:studentId",
										element: <WrittenAssessmentPage />,
									},
									{
										path: "/courses/:courseId/witness-incident/:incidentId",
										element: <WitnessIncidentPage />,
									},
									{
										path: "/courses/:courseId/certificates",
										element: <CertificatesPage />,
									},
								],
							},
						],
					},
				],
			},
		],
	},
	{
		element: <AuthLayout />,
		children: [
			{ path: "/login", element: <Login /> },
			{ path: "/forgotten-password", element: <ForgottenPassword /> },
			{ path: "/reset-password", element: <ResetPassword /> },
			{ path: "/token-login", element: <LoginAsTutor /> },
		],
	},
]);

function LogoutComponent() {
	const { api } = useAppContext();
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		async function logout() {
			const { error } = await api.logout();
			if (!error) {
				setLoading(false);
			}
		}

		logout();
	}, []);

	if (loading) {
		return <div>Logging out...</div>;
	}

	return <Navigate to="/login" replace />;
}

function CoursesLoader() {
	const { state, api } = useAppContext();

	useEffect(() => {
		api.loadCourses();
	}, []);

	if (state.loading_courses) {
		return (
			<div className="courses-loading-container">
				<LoadingSpinner text={"Loading Course Data..."} />
			</div>
		);
	}

	if (state.courses_loading_error) {
		return <div>{state.courses_loading_error}</div>;
	}

	return <Outlet />;
}

function findCourseById(courses, courseId) {
	// eslint-disable-next-line eqeqeq
	return courses.find((course) => course.id == courseId);
}

function CourseLoader() {
	const { courseId } = useParams();
	const navigate = useNavigate();
	const { state, dispatch, api } = useAppContext();

	useEffect(() => {
		if (courseId && state?.courses) {
			let course = findCourseById(state.courses, courseId);
			if (course) {
				let date = findCurrentDate(course);
				// console.log(date)
				dispatch({
					type: "SET_COURSE_AND_DATE",
					payload: { course, date },
				});
			} else {
				// If course id is not found, navigate to dashboard
				dispatch({ type: "SET_COURSE", payload: null });
				dispatch({ type: "SET_DATE", payload: null });
				// Navigate to dashboard with error param
				navigate("/", { replace: true, state: { error: "Course not found" } });
			}
		}
	}, [courseId]);

	function findCurrentDate(course) {
		if (
			!course?.dates ||
			!Array.isArray(course.dates) ||
			!course.dates.length
		) {
			return null;
		}
		// If there is only one date, return that
		if (course.dates.length === 1) {
			return course.dates[0];
		}

		// Try and find the corresponding date for today
		let today = new Date();
		let todayString = today.toISOString().split("T")[0];
		let date = course.dates.find((date) => date.date === todayString);
		if (date) {
			return date;
		}

		// Find in local storage if there is a date saved for this course
		let savedDate = localStorage.getItem("pta_date");
		if (savedDate) {
			let parsedSavedDate = JSON.parse(savedDate);
			// Check parsed date has course_id and date_id
			if (parsedSavedDate.course_id && parsedSavedDate.date_id) {
				// Check parsed date course_id matches current course id
				// eslint-disable-next-line eqeqeq
				if (parsedSavedDate.course_id == course.id) {
					// Find date in course with date_id
					let date = course.dates.find(
						// eslint-disable-next-line eqeqeq
						(date) => date.id == parsedSavedDate.date_id
					);
					if (date) {
						return date;
					}
				}
			}
		}

		// If we can't find the corresponding date, find the closest date and return that
		let parsedToday = Date.parse(todayString);
		let dates = course.dates.map((date) => Date.parse(date.date));
		let closestDate = dates.reduce((prev, curr) => {
			return Math.abs(curr - parsedToday) < Math.abs(prev - parsedToday)
				? curr
				: prev;
		});

		return course.dates.find((date) => Date.parse(date.date) === closestDate);
	}

	if (!api.getCurrentCourse()) {
		return <div>Loading Course...</div>;
	}

	return <Outlet />;
}

function App() {
	return (
		<ContextProvider>
			<RouterProvider router={router} />
		</ContextProvider>
	);
}

function RequireAuth() {
	let { state, api } = useContext(AppContext);
	let location = useLocation();
	const [loading, setLoading] = useState(true);

	// On first page load, not when page changes, run isLoggedin
	useEffect(() => {
		if (!state.logged_in) {
			isLoggedin();
		} else {
			setLoading(false);
		}
	}, []);

	async function isLoggedin() {
		await api.isLoggedin();
		setLoading(false);
	}

	if (loading) {
		return (
			<div className="authenticating-container">
				<div className="loading-container">
					<LoadingSpinner text={"Authenticating..."} />
				</div>
			</div>
		);
	}

	if (!loading && !state.logged_in) {
		return <Navigate to="/login" state={{ from: location }} replace />;
	}

	return <Outlet />;
}

function ContextProvider({ children }) {
	let [state, dispatch] = useImmerReducer(reducer, initialState);

	// Listener to check if browser is online or offline and update state
	useEffect(() => {
		function handleNetworkChange() {
			console.log("Network change", navigator.onLine);
			if (navigator.onLine) {
				dispatch({ type: "SET_ONLINE" });
			} else {
				dispatch({ type: "SET_OFFLINE" });
			}
		}
		handleNetworkChange()
		window.addEventListener("online", handleNetworkChange);
		window.addEventListener("offline", handleNetworkChange);
	}, []);

	async function fetchData(url, method = "GET", data) {
		const result = { data: null, error: null };
		let token = getToken();

		const headers = {
			"Content-Type": "application/json",
		};

		if (token) {
			headers["Authorization"] = `Bearer ${token}`;
		}

		try {
			let response = await fetch(`${API_URL}${url}`, {
				method: method,
				cache: "no-store",
				headers: headers,
				body: JSON.stringify(data),
			});
			// Parse PDF response
			if (response.headers.get("content-type") === "application/pdf") {
				// Return PDF as base64 string
				let blob = await response.blob();
				result.data = blob;
				return result;
			}

			// Try to parse as JSON regardless of Content-Type header
			try {
				const text = await response.text();
				try {
					const json = JSON.parse(text);
					result.data = json;
				} catch (e) {
					console.error("Non-JSON response:", text);
					result.error = "Server Error: Unexpected response format";
				}
			} catch (error) {
				result.error = error.message;
			}

			return result;
		} catch (error) {
			console.error("Fetch error:", error);
			result.error = error.message || "An unexpected error occurred";
			return result;
		}
	}

	async function isLoggedin() {
		var isOffline = state.offline || !navigator.onLine;

		if (isOffline) {
			// Check if user token is present
			var userToken = getToken();
			var userDetails = getUserDetails();

			if (userToken && userDetails) {
				dispatch({ type: "LOGIN", payload: userDetails });
				return true;
			} else {
				dispatch({ type: "LOGOUT" });
				return false;
			}
		} else {
			const { data, error } = await fetchData("/user-info");

			if (!error) {
				// if data has logged_in set and is false
				if (data?.logged_in === false) {
					dispatch({ type: "LOGOUT" });
					return false;
				}
				dispatch({ type: "LOGIN", payload: createUserData(data) });
				return true;
			} else {
				dispatch({ type: "LOGOUT" });
				return false;
			}
		}
	}

	async function login(email, password) {
		const { data, error } = await fetchData("/login", "POST", {
			email,
			password,
		});

		if (!error) {
			if (data?.status) {
				dispatch({ type: "LOGIN", payload: createUserData(data) });
				setToken(data.token);
				return { success: true };
			} else {
				return { error: data?.error || "An error occurred" };
			}
		} else {
			return { error: error.message };
		}
	}

	async function logout() {
		const isOffline = state.offline;

		if (isOffline) {
			dispatch({ type: "LOGOUT" });
			deleteToken();
			clearOfflineData();

			return { success: true };
		}

		const { data, error } = await fetchData("/logout", "POST");

		if (!error) {
			if (data?.status) {
				dispatch({ type: "LOGOUT" });
				deleteToken();
				return { success: true };
			} else {
				return { error: data?.error || "An error occurred" };
			}
		} else {
			return { error: error.message };
		}
	}

	async function loadCourses() {
		var isOffline = state.offline;
		console.log(isOffline);
		var user_id = state.user.id;

		if (!isOffline) {
			const { data, error } = await fetchData("/courses-for-tutor");
			dispatch({ type: "SET_LOADING_COURSES" });

			if (!error) {
				if (data?.terms) {
					dispatch({ type: "SET_TERMS_TEXT", payload: data.terms });
				}
				if (data?.privacy_policy) {
					dispatch({ type: "SET_PRIVACY_TEXT", payload: data.privacy_policy });
				}
				if (data?.cookies) {
					dispatch({ type: "SET_COOKIES_TEXT", payload: data.cookies });
				}
				if (data?.this_week) {
					setOfflineCourses(data, user_id);
					dispatch({ type: "SET_COURSES", payload: data.this_week });
					dispatch({ type: "SET_UPCOMING_COURSES", payload: data.future });
					dispatch({ type: "SET_PAST_COURSES", payload: data.past });
					dispatch({ type: "SET_FORMS_EXTRA_CONTENT", payload: data.form_content });
					return true;
				} else {
					dispatch({
						type: "SET_COURSES_LOADING_ERROR",
						payload: data?.error || "An error occurred",
					});
					return false;
				}
			} else {
				dispatch({
					type: "SET_COURSES_LOADING_ERROR",
					payload: error.message,
				});
				return false;
			}
		} else {
			dispatch({ type: "SET_LOADING_COURSES" });
			var offlineCourses = getOfflineCourses(user_id);

			if (offlineCourses) {
				dispatch({ type: "SET_COURSES", payload: offlineCourses.this_week });
				dispatch({ type: "SET_UPCOMING_COURSES", payload: offlineCourses.future });
				dispatch({ type: "SET_PAST_COURSES", payload: offlineCourses.past });
				dispatch({ type: "SET_FORMS_EXTRA_CONTENT", payload: offlineCourses.form_content });
				return true;
			} else {
				dispatch({
					type: "SET_COURSES_LOADING_ERROR",
					payload: "No offline courses found",
				});
				return false;
			}
		}
	}

	function setToken(token) {
		localStorage.setItem("pta_token", token);
	}

	function deleteToken() {
		localStorage.removeItem("pta_token");
	}

	function getToken() {
		return localStorage.getItem("pta_token");
	}

	function setUserDetails(details) {
		secureLocalStorage.setItem("pta_user", details);
	}

	function getUserDetails() {
		var details = secureLocalStorage.getItem("pta_user");
		return details;
	}

	function deleteUserDetails() {
		secureLocalStorage.removeItem("pta_user");
	}

	function setOfflineCourses(courses, user_id) {
		secureLocalStorage.setItem("pta_offline_courses_" + user_id, courses);
	}

	function getOfflineCourses(user_id) {
		var courses = secureLocalStorage.getItem("pta_offline_courses_" + user_id);
		return courses;
	}

	function deleteOfflineCourses(user_id) {
		secureLocalStorage.removeItem("pta_offline_courses_" + user_id);
	}

	function clearOfflineData() {
		secureLocalStorage.clear();
	}

	async function clearCache() {
		if (state?.user?.id) {
			deleteOfflineCourses(state.user.id);
		}

		// Clear all CacheStorage caches (Workbox app shell, images, etc.)
		if ('caches' in window) {
			try {
				const cacheNames = await caches.keys();
				await Promise.all(
					cacheNames.map(cacheName => caches.delete(cacheName))
				);
			} catch (e) {
				console.error("Error clearing caches", e);
			}
		}

		// Unregister all service workers to ensure next load gets fresh ones
		if ('serviceWorker' in navigator) {
			try {
				const registrations = await navigator.serviceWorker.getRegistrations();
				for (let registration of registrations) {
					await registration.unregister();
				}
			} catch (e) {
				console.error("Error unregistering service workers", e);
			}
		}

		// Force complete hard reload from server
		window.location.reload(true);
	}

	function getCurrentCourse() {
		// Find current course in state.courses using state.current_course_id
		if (!state?.courses || !state.current_course_id) {
			return null;
		}
		return findCourseById(state.courses, state.current_course_id);
	}

	function getCurrentDate() {
		// Find current date in current course using state.current_date_id
		if (
			!state?.courses ||
			!state.current_course_id ||
			!state.current_date_id
		) {
			return null;
		}
		let course = findCourseById(state.courses, state.current_course_id);
		if (
			!course?.dates ||
			!Array.isArray(course.dates) ||
			!course.dates.length
		) {
			return null;
		}

		return course.dates.find((date) => date.id === state.current_date_id);
	}

	function createUserData(data) {
		var userData = {
			id: data.id,
			first_name: data.first_name,
			last_name: data.last_name,
			email: data.email,
			read_only: data?.read_only || false,
		};

		setUserDetails(userData);
		return userData;
	}

	function findStudentById(id) {
		id = parseInt(id);
		let course = getCurrentCourse();
		let date = getCurrentDate();
		if (!course || !date) {
			return null;
		}
		// eslint-disable-next-line eqeqeq
		let student = date.attendance.find(
			(student) => student.student_id == id
		);
		return student;
	}

	function findHealthQuestionnaireById(id) {
		id = parseInt(id);
		let course = getCurrentCourse();
		let date = getCurrentDate();
		if (!course || !date) {
			return null;
		}

		let attendance = date.attendance;
		// Loop attendance, each attendance record has a health_questionnaire object, check if id matches
		for (let i = 0; i < attendance.length; i++) {
			// eslint-disable-next-line eqeqeq
			if (attendance[i]?.health_questionnaire?.form?.id == id) {
				return attendance[i].health_questionnaire;
			}
		}
		return null;
	}

	function findReferralForm(studentId) {
		let course = getCurrentCourse();
		let date = getCurrentDate();
		if (!course || !date) {
			return null;
		}
		let student = findStudentById(studentId);
		if (!student) {
			return null;
		}
		let referralForm = student?.referral_form;
		return referralForm;
	}

	function findStudentOnCourseById(id) {
		id = parseInt(id);
		let course = getCurrentCourse();
		// eslint-disable-next-line eqeqeq
		let student = course.students.find((student) => student.id == id);
		return student;
	}

	function changeDate(date) {
		dispatch({ type: "SET_DATE", payload: date });
		// Set local storage to remember date with course id and date id as value
		localStorage.setItem(
			"pta_date",
			JSON.stringify({ course_id: getCurrentCourse().id, date_id: date.id })
		);
	}

	const api = {
		login,
		logout,
		fetchData,
		isLoggedin,
		loadCourses,
		getCurrentCourse,
		getCurrentDate,
		changeDate,
		findStudentById,
		findStudentOnCourseById,
		setToken,
		findHealthQuestionnaireById,
		findReferralForm,
		clearCache
	};

	return (
		<AppContext.Provider value={{ api, state, dispatch }}>
			{children}
		</AppContext.Provider>
	);
}

export default App;
